import { useState } from 'react';
import type { ResourceCategory, ResourceItem } from '@/mocks/resources';

interface HeaderProps {
  categories: ResourceCategory[];
  savedItems: ResourceItem[];
  onRemoveSaved: (id: string) => void;
  onJump: (id: string) => void;
}

const Header = ({ categories, savedItems, onRemoveSaved, onJump }: HeaderProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [savedOpen, setSavedOpen] = useState(false);

  const navIds = ['efficiency', 'design', 'ai', 'template', 'useful'];

  return (
    <header className="fixed top-0 inset-x-0 z-40 border-b border-background-200/80 bg-background-50/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6">
        <a href="#top" className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-compass-3-line text-xl" aria-hidden="true" />
          </span>
          <span className="font-heading text-2xl font-bold tracking-tight text-foreground-950">
            51全网
          </span>
        </a>

        {/* 桌面导航 */}
        <nav className="hidden items-center gap-1 lg:flex" aria-label="站点导航">
          {categories
            .filter((c) => navIds.includes(c.id))
            .map((c) => (
              <a
                key={c.id}
                href={`#${c.id}`}
                onClick={() => onJump(c.id)}
                className="whitespace-nowrap rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-700"
              >
                {c.name}
              </a>
            ))}
          <a
            href="#faq"
            className="whitespace-nowrap rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-700"
          >
            常见问题
          </a>
        </nav>

        <div className="flex items-center gap-2">
          {/* 收藏夹 */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setSavedOpen((v) => !v)}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 hover:border-primary-300 hover:text-primary-700"
              aria-expanded={savedOpen}
              aria-label="我的收藏夹"
            >
              <i className="ri-bookmark-line text-base" aria-hidden="true" />
              <span className="hidden sm:inline">收藏夹</span>
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-500 px-1 text-xs font-bold text-background-50">
                {savedItems.length}
              </span>
            </button>
            {savedOpen && (
              <div className="absolute right-0 top-12 w-72 rounded-lg border border-background-200 bg-background-50 p-2 shadow-none">
                <div className="flex items-center justify-between px-2 py-1.5">
                  <p className="text-sm font-semibold text-foreground-950">我收藏的资源</p>
                  {savedItems.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSavedOpen(false)}
                      className="rounded-md px-1.5 py-0.5 text-xs text-foreground-600 hover:text-primary-700"
                    >
                      关闭
                    </button>
                  )}
                </div>
                <div className="max-h-64 overflow-auto">
                  {savedItems.length === 0 ? (
                    <p className="px-2 py-4 text-center text-sm text-foreground-600">
                      还没有收藏，点击资源卡片上的收藏即可保存
                    </p>
                  ) : (
                    savedItems.map((it) => (
                      <div
                        key={it.id}
                        className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-background-100"
                      >
                        <i className={`${it.icon} text-base text-primary-600`} aria-hidden="true" />
                        <span className="flex-1 truncate text-sm text-foreground-800">{it.name}</span>
                        <button
                          type="button"
                          onClick={() => onRemoveSaved(it.id)}
                          className="rounded p-1 text-foreground-500 hover:text-primary-600"
                          aria-label={`取消收藏 ${it.name}`}
                        >
                          <i className="ri-close-line text-sm" aria-hidden="true" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 移动端菜单按钮 */}
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-800 lg:hidden"
            aria-label="打开菜单"
            aria-expanded={menuOpen}
          >
            <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* 移动端菜单 */}
      {menuOpen && (
        <nav className="border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden" aria-label="移动端导航">
          <div className="grid grid-cols-2 gap-1">
            {categories.map((c) => (
              <a
                key={c.id}
                href={`#${c.id}`}
                onClick={() => {
                  setMenuOpen(false);
                  onJump(c.id);
                }}
                className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-800 hover:bg-background-100"
              >
                <i className={c.list.length > 0 ? `ri-circle-fill text-[6px] text-primary-500` : ''} aria-hidden="true" />
                {c.name}
              </a>
            ))}
            <a
              href="#faq"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-800 hover:bg-background-100"
            >
              <i className="ri-question-line text-base text-primary-500" aria-hidden="true" />
              常见问题
            </a>
            <a
              href="#about"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-800 hover:bg-background-100"
            >
              <i className="ri-information-line text-base text-primary-500" aria-hidden="true" />
              关于我们
            </a>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;